import { action, computed, flow, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { ILanguageTranslation } from 'src/types/languageTranslation';
import { LoadingStatus, paginationCast, TPagination } from 'src/types/main';
import {
  defaultLanguageTranslation,
  defaultLanguageTranslationsFilters,
  defaultLanguageTranslationValidation,
} from 'src/view/screens/SettingsScreen/constants/languageTranslations';
import {
  ILanguageTranslationsFilters,
  ILanguageTranslationValidation,
  LANGUAGE_TRANSLATIONS_TYPE,
} from 'src/view/screens/SettingsScreen/screens/General/LanguageCountryCurrency/types/languageTranslations';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

class LanguageTranslationStore {
  constructor() {
    makeObservable(this);
  }

  getLanguageTranslationAbortController = new AbortController();
  languageTranslationsGetTimeoutId: NodeJS.Timeout | null = setTimeout(() => {
    return;
  }, 0);

  @observable public languageTranslationsLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public languageTranslationLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public languageTranslationDeleteLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public languageTranslationCreateLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public languageTranslationUpdateLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @persist @observable languageTranslations: ILanguageTranslation[] = [];
  @persist @observable public pagination: TPagination = { currentPage: 0, lastPage: 0, totalRows: 0, perPage: 0 };
  @persist @observable languageTranslation: ILanguageTranslation | null = null;
  @observable filters: ILanguageTranslationsFilters = defaultLanguageTranslationsFilters;
  @observable edited: boolean = false;
  @observable validation: ILanguageTranslationValidation = defaultLanguageTranslationValidation;

  @action setFilters = (filters: Partial<ILanguageTranslationsFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this._getLanguageTranslationsWithDelay();
  };

  @computed get isValid(): boolean {
    return this.validation.key.state && this.validation.type.state && this.validation.translations.en.state && this.validation.translations.ar.state;
  }

  private _getLanguageTranslations = flow(function* (this: LanguageTranslationStore, pageNumber: number = 1): any {
    this.getLanguageTranslationAbortController.abort();
    this.getLanguageTranslationAbortController = getAbortController();

    try {
      this.languageTranslationsLoadingStatus = LoadingStatus.Loading;

      const params: any = {
        page: pageNumber,
        search: this.filters.search || '',
        type: this.filters.type || LANGUAGE_TRANSLATIONS_TYPE.BACKEND,
        per_page: this.filters.perPage || undefined,
        sort_field: this.filters.sortField || undefined,
        sort_order: this.filters.sortDir || undefined,
      };

      const response = yield client.get('v4/translations', {
        params,
        headers: { 'Accept-Language': 'en,ar' },
        signal: this.getLanguageTranslationAbortController.signal,
      });

      this.languageTranslations = response.data;
      this.pagination = paginationCast(response);
      this.languageTranslationsLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.languageTranslationsLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.languageTranslationsLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'LanguageTranslationStore _getLanguageTranslations', showModal: true });
    }
  }).bind(this);

  @action getLanguageTranslation = flow(function* (this: LanguageTranslationStore, translationId: number | string): any {
    try {
      this.languageTranslationLoadingStatus = LoadingStatus.Loading;

      const response = yield client.get(`v4/translations/${translationId}`, { headers: { 'Accept-Language': 'en,ar' } });

      this.setLanguageTranslation(response);
      this.languageTranslationLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.languageTranslationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'LanguageTranslationStore getLanguageTranslation', showModal: true });
    }
  }).bind(this);

  @action getLanguageTranslations = () => {
    if (this.languageTranslationsLoadingStatus === LoadingStatus.Init) {
      this._getLanguageTranslationsWithNoDelay();
    } else {
      this._getLanguageTranslationsWithDelay();
    }
  };

  @action private _getLanguageTranslationsWithNoDelay = (pageNumber: number = 1) => {
    if (this.languageTranslationsGetTimeoutId) {
      clearTimeout(this.languageTranslationsGetTimeoutId);
    }
    this._getLanguageTranslations(pageNumber);
  };

  @action private _getLanguageTranslationsWithDelay = (pageNumber: number = 1) => {
    if (this.languageTranslationsGetTimeoutId) {
      clearTimeout(this.languageTranslationsGetTimeoutId);
    }
    this.languageTranslationsGetTimeoutId = setTimeout(() => {
      this._getLanguageTranslations(pageNumber);
    }, 700);
  };

  @action public changePage = (pageNumber: number) => {
    this._getLanguageTranslationsWithNoDelay(pageNumber);
  };

  @action public setLanguageTranslation = (languageTranslation?: ILanguageTranslation) => {
    this.languageTranslation = languageTranslation ? languageTranslation : defaultLanguageTranslation;
    this.languageTranslationLoadingStatus = LoadingStatus.Success;
    this.languageTranslationDeleteLoadingStatus = LoadingStatus.Init;
    this.languageTranslationCreateLoadingStatus = LoadingStatus.Init;
    this.languageTranslationUpdateLoadingStatus = LoadingStatus.Init;
    this.validation = defaultLanguageTranslationValidation;
  };

  private clearValidationKey(key: string) {
    if (!Object.keys(this.validation).includes(key)) {
      return;
    }
    if (key === 'translations') {
      this.validation = {
        ...this.validation,
        [key]: defaultLanguageTranslationValidation.translations,
      };
    } else {
      this.validation = {
        ...this.validation,
        [key]: {
          state: true,
          errorText: undefined,
        } as Partial<ILanguageTranslationValidation>,
      };
    }
  }

  @action public changeLanguageTranslationHandler = (value: Partial<ILanguageTranslation>) => {
    if (!this.languageTranslation) return;
    const key = Object.keys(value)[0];
    this.clearValidationKey(key);
    this.languageTranslation = { ...this.languageTranslation, ...value };
    this.edited = true;
  };

  @action public deleteLanguageTranslation = async (languageTranslationId: number) => {
    try {
      this.languageTranslationsLoadingStatus = LoadingStatus.Refreshing;
      this.languageTranslationDeleteLoadingStatus = LoadingStatus.Loading;

      await client.delete(`v4/translations/${languageTranslationId}`);

      if (this.pagination.currentPage > 0) {
        this._getLanguageTranslations(this.pagination.currentPage);
      }

      this.languageTranslationDeleteLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.languageTranslationDeleteLoadingStatus = LoadingStatus.Error;
      this.languageTranslationsLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'LanguageTranslationStore deleteLanguageTranslation', showModal: true });
    }
  };

  @action public createLanguageTranslation = async () => {
    if (!this.languageTranslation) return;

    try {
      this.languageTranslationCreateLoadingStatus = LoadingStatus.Loading;

      const response = await client.post('v4/translations', { ...this.languageTranslation }, { headers: { 'Accept-Language': 'en,ar' } });

      this.languageTranslation.id = response.id;
      this.edited = false;
      this.languageTranslationCreateLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.languageTranslationCreateLoadingStatus = LoadingStatus.Error;

      if (this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'LanguageTranslationStore createLanguageTranslation', showModal: true });
    }
  };

  @action public updateLanguageTranslation = async () => {
    if (!this.languageTranslation?.id) {
      return;
    }
    try {
      this.languageTranslationLoadingStatus = LoadingStatus.Loading;
      this.languageTranslationUpdateLoadingStatus = LoadingStatus.Loading;

      await client.put(
        `v4/translations/${this.languageTranslation.id}`,
        { ...this.languageTranslation },
        { headers: { 'Accept-Language': 'en,ar' } }
      );

      this.edited = false;
      this.languageTranslationLoadingStatus = LoadingStatus.Success;
      this.languageTranslationUpdateLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.languageTranslationLoadingStatus = LoadingStatus.Error;
      this.languageTranslationUpdateLoadingStatus = LoadingStatus.Error;

      if (this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'LanguageTranslationStore updateLanguageTranslation', showModal: true });
    }
  };

  private isErrorResponseHandled(error: any): boolean {
    const errorsBag = error.response.data;
    let hasApiErrors = false;
    if (errorsBag.key) {
      this.validation.key.errorText = errorsBag.key[0];
      this.validation.key.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.type) {
      this.validation.type.errorText = errorsBag.type[0];
      this.validation.type.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.translations && errorsBag.translations.en) {
      this.validation.translations.en.errorText = errorsBag.translations.en[0];
      this.validation.translations.en.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.translations && errorsBag.translations.ar) {
      this.validation.translations.ar.errorText = errorsBag.translations.ar[0];
      this.validation.translations.ar.state = false;
      hasApiErrors = true;
    }
    return hasApiErrors;
  }

  @action unMount = () => {
    this.languageTranslation = null;
    this.edited = false;

    if (!window.location.pathname.includes(SCREENS.SETTINGS_CURRENCY())) this.filters = defaultLanguageTranslationsFilters;
  };
}

const languageTranslationStore = new LanguageTranslationStore();
export default languageTranslationStore;
